import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { SideBySideDebtsPanel } from '../../debts/components/SideBySideDebtsPanel';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { PageHeader } from '../../../shared/ui/PageHeader';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { request } from '../../../shared/api/client';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { loadContactOpenDebts, summarizeContactBalance } from '../../debts/lib/contactBalance';
import {
  useContactTrades,
  type ContactTradeItem,
  type PaginatedResponse,
} from '../../trades/api/useTrades';
import {
  useArchiveContact,
  useContact,
  useUnarchiveContact,
  useUpdateContact,
  type Contact,
} from '../api/useContacts';
import { ContactForm } from '../components/ContactForm';

type Tab = 'overview' | 'debts' | 'trades';

const TAB_ORDER: Tab[] = ['overview', 'debts', 'trades'];

const PAGE_STEP = 20;

export function ContactProfilePage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const q = useContact(id);
  const update = useUpdateContact(id);
  const archive = useArchiveContact(id);
  const unarchive = useUnarchiveContact(id);
  const [editing, setEditing] = useState(false);
  // Default to trades tab so clicking a contact shows their history immediately.
  const [tab, setTab] = useState<Tab>('trades');

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) return <EmptyState icon="👤" title={t('contacts.profile.not_found')} />;
  const c = q.data;

  if (editing) {
    return (
      <>
        <PageHeader title={t('contacts.edit')} />
        <ContactForm
          initial={c}
          onSubmit={async (input) => {
            await update.mutateAsync(input);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
          submitting={update.isPending}
          error={update.error}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={c.name}
        action={
          <button type="button" className="btn btn--secondary" onClick={() => setEditing(true)}>
            {t('common.edit')}
          </button>
        }
      />

      <nav className="tabs" role="tablist" aria-label={t('contacts.title')}>
        {TAB_ORDER.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            aria-current={tab === key ? 'page' : undefined}
            className="tabs__item"
            onClick={() => setTab(key)}
          >
            {t(`contacts.profile.${key}`)}
          </button>
        ))}
      </nav>

      <div role="tabpanel">
        {tab === 'overview' ? <OverviewPanel contact={c} /> : null}
        {tab === 'debts' ? <SideBySideDebtsPanel contactId={id} /> : null}
        {tab === 'trades' ? (
          <ContactTradesTab contactId={id} contactName={c.name} contactPhone={c.phone} />
        ) : null}
      </div>

      <div className="profile-actions">
        {c.isArchived ? (
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => unarchive.mutate()}
            disabled={unarchive.isPending}
          >
            {t('contacts.unarchive')}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn--danger"
            onClick={async () => {
              await archive.mutateAsync();
              navigate('/contacts');
            }}
            disabled={archive.isPending}
          >
            {t('contacts.archive')}
          </button>
        )}
      </div>
    </>
  );
}

function ContactTradesTab({
  contactId,
  contactName,
  contactPhone,
}: {
  contactId: string;
  contactName: string;
  contactPhone: string | null;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [limit, setLimit] = useState(PAGE_STEP);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<Error | null>(null);
  const q = useContactTrades(contactId, limit);
  const currencies = useCurrencies(true);
  const currencyCode = (id: string) =>
    currencies.data?.find((b) => b.id === id)?.code ?? id.slice(0, 3);

  async function handleExport() {
    // Open immediately so browsers don't treat it as a popup.
    const win = window.open('', '_blank');
    if (!win) return;
    setExportError(null);
    setExporting(true);
    try {
      const trades: ContactTradeItem[] = [];
      let tradeTotal = 0;
      do {
        const page = await request<PaginatedResponse<ContactTradeItem>>(
          `/contacts/${contactId}/trades?limit=100&offset=${trades.length}`,
        );
        trades.push(...page.data);
        tradeTotal = page.total;
        if (page.data.length === 0) break;
      } while (trades.length < tradeTotal);
      const debts = await loadContactOpenDebts(contactId);
      const balancesByCurrency = summarizeContactBalance(debts.receivables, debts.payables);

      const dir = i18n.dir();
      const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });
      const exportedOn = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'long' }).format(
        new Date(),
      );

      const esc = (s: string) =>
        s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

      const balanceRows = balancesByCurrency
        .map(
          (balance) => `<tr>
        <td>${esc(currencyCode(balance.currencyId))}</td>
        <td class="r mono">${esc(balance.receivable.toFixed(2))}</td>
        <td class="r mono">${esc(balance.payable.toFixed(2))}</td>
        <td class="r mono total">${balance.balance.isPositive() ? '+' : ''}${esc(balance.balance.toFixed(2))} ${esc(currencyCode(balance.currencyId))}</td>
      </tr>`,
        )
        .join('');

      const openDebtRows = [
        ...debts.receivables.map((debt) => ({ debt, kind: t('debts.tab_receivables') })),
        ...debts.payables.map((debt) => ({ debt, kind: t('debts.tab_payables') })),
      ]
        .sort((a, b) => b.debt.createdAt.localeCompare(a.debt.createdAt))
        .map(
          ({ debt, kind }) => `<tr>
          <td>${esc(dateFmt.format(new Date(debt.createdAt)))}</td>
          <td>${esc(kind)}</td>
          <td class="r mono">${esc(debt.outstandingAmount)} ${esc(currencyCode(debt.currencyId))}</td>
        </tr>`,
        )
        .join('');

      const rows = trades
        .map((item) => {
          const isIn = item.kind === 'purchase';
          const delivCode = currencyCode(item.deliveredCurrencyId);
          const payCode = currencyCode(item.paymentCurrencyId);
          const payStatus =
            item.kind === 'purchase'
              ? t(`purchases.payment_status.${item.paymentStatus}`)
              : t(`sales.payment_status.${item.paymentStatus}`);
          return `<tr>
            <td class="mono">${esc(dateFmt.format(new Date(item.transactionDate)))}</td>
            <td class="${isIn ? 'in' : 'out'}">${esc(isIn ? t('purchases.title') : t('sales.title'))}</td>
            <td class="mono r">${esc(parseFloat(item.deliveredAmount).toFixed(2))} ${esc(delivCode)}</td>
            <td class="mono r">${esc(parseFloat(item.rate).toFixed(4))}</td>
            <td class="mono r">${esc(parseFloat(item.paymentTotal).toFixed(2))} ${esc(payCode)}</td>
            <td class="mono r">${esc(parseFloat(item.outstandingAmount).toFixed(2))} ${esc(payCode)}</td>
            <td class="${payStatusClass(item.paymentStatus)}">${esc(payStatus)}</td>
            <td>${esc(item.reference ?? '')}</td>
          </tr>`;
        })
        .join('');

      const html = `<!DOCTYPE html>
<html lang="${i18n.language}" dir="${dir}">
<head>
<meta charset="UTF-8">
<title>${esc(contactName)}</title>
<style>
*{box-sizing:border-box}
body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#111;margin:0;padding:24px}
h1{font-size:20px;font-weight:700;margin:0 0 2px}
h2{font-size:13px;margin:22px 0 8px}
.sub{font-size:11px;color:#555;margin:0 0 16px}
.note{color:#555;margin:7px 0 0}
table{width:100%;border-collapse:collapse}
th{background:#111;color:#fff;text-align:start;padding:6px 8px;font-size:10px;
   text-transform:uppercase;letter-spacing:.05em;white-space:nowrap}
td{padding:5px 8px;border-bottom:1px solid #e8e8e8;vertical-align:middle;white-space:nowrap}
thead{display:table-header-group}
tr{break-inside:avoid}
.total{font-weight:700;font-size:13px}
.balance-table{max-width:650px}
tr:nth-child(even) td{background:#f7f7f7}
.mono{font-family:'Courier New',monospace}
.r{text-align:end}
.in{color:#0a7c42}
.out{color:#c2410c}
.warn{color:#b45309}
.danger{color:#c2410c}
@media print{
  @page{margin:12mm 15mm;size:A4 landscape}
  body{padding:0}
  button{display:none}
}
</style>
</head>
<body>
<h1>${esc(contactName)}</h1>
<p class="sub">${contactPhone ? `${esc(contactPhone)} · ` : ''}${esc(exportedOn)}</p>
<h2>${esc(t('contacts.profile.pdf_balance_title'))}</h2>
<table class="balance-table"><thead><tr>
  <th>${esc(t('expenses.currency'))}</th>
  <th>${esc(t('debts.tab_receivables'))}</th>
  <th>${esc(t('debts.tab_payables'))}</th>
  <th>${esc(t('contacts.profile.pdf_balance'))}</th>
</tr></thead><tbody>${balanceRows || `<tr><td colspan="4">${esc(t('debts.no_open_balance'))}</td></tr>`}</tbody></table>
<p class="note">${esc(t('contacts.profile.pdf_balance_note'))}</p>
<h2>${esc(t('contacts.profile.pdf_open_debts'))}</h2>
<table class="balance-table"><thead><tr>
  <th>${esc(t('purchases.date'))}</th>
  <th>${esc(t('contacts.profile.pdf_type'))}</th>
  <th>${esc(t('debts.outstanding_label'))}</th>
</tr></thead><tbody>${openDebtRows || `<tr><td colspan="3">${esc(t('debts.no_open_balance'))}</td></tr>`}</tbody></table>
<h2>${esc(t('contacts.profile.trades'))}</h2>
<table>
<thead><tr>
  <th>${esc(t('purchases.date'))}</th>
  <th>${esc(t('contacts.profile.pdf_type'))}</th>
  <th>${esc(t('purchases.delivered_amount'))}</th>
  <th>${esc(t('purchases.rate_label'))}</th>
  <th>${esc(t('purchases.payment_total'))}</th>
  <th>${esc(t('purchases.outstanding'))}</th>
  <th>${esc(t('contacts.profile.pdf_pay_status'))}</th>
  <th>${esc(t('purchases.reference'))}</th>
</tr></thead>
<tbody>${rows || `<tr><td colspan="8">${esc(t('contacts.profile.trades_empty'))}</td></tr>`}</tbody>
</table>
<script>window.onload=function(){window.print()}</script>
</body>
</html>`;

      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (error) {
      win.close();
      setExportError(error instanceof Error ? error : new Error(t('errors.unknown')));
    } finally {
      setExporting(false);
    }
  }

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorMessage error={q.error} />;
  const dateFmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBlockEnd: 'var(--sp-3)' }}>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? t('common.loading') : t('contacts.profile.export_pdf')}
        </button>
      </div>
      {exportError ? <ErrorMessage error={exportError} /> : null}

      {!q.data || q.data.data.length === 0 ? (
        <EmptyState icon="🧾" title={t('contacts.profile.trades_empty')} />
      ) : (
        <div className="table-scroll">
          <table className="data-table" aria-label={t('contacts.profile.trades')}>
            <thead>
              <tr>
                <th>{t('purchases.date')}</th>
                <th>Type</th>
                <th style={{ textAlign: 'end' }}>{t('purchases.delivered_amount')}</th>
                <th style={{ textAlign: 'end' }}>{t('purchases.rate_label')}</th>
                <th style={{ textAlign: 'end' }}>{t('purchases.payment_total')}</th>
                <th style={{ textAlign: 'end' }}>{t('purchases.outstanding')}</th>
                <th>{t('contacts.profile.pdf_pay_status')}</th>
                <th>{t('purchases.reference')}</th>
              </tr>
            </thead>
            <tbody>
              {q.data?.data.map((item) => {
                const path =
                  item.kind === 'purchase' ? `/purchases/${item.id}` : `/sales/${item.id}`;
                const isIn = item.kind === 'purchase';
                const badge = isIn ? t('purchases.title') : t('sales.title');
                const payStatusLabel =
                  item.kind === 'purchase'
                    ? t(`purchases.payment_status.${item.paymentStatus}`)
                    : t(`sales.payment_status.${item.paymentStatus}`);
                const onKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(path);
                  }
                };
                return (
                  <tr
                    key={`${item.kind}-${item.id}`}
                    role="link"
                    tabIndex={0}
                    onClick={() => navigate(path)}
                    onKeyDown={onKey}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="card-row__mono">
                      {dateFmt.format(new Date(item.transactionDate))}
                    </td>
                    <td>
                      <span className={`badge ${isIn ? 'badge--in' : 'badge--out'}`}>{badge}</span>
                    </td>
                    <td className="card-row__mono" style={{ textAlign: 'end' }}>
                      {item.deliveredAmount}{' '}
                      <span className="card-row__currency">
                        {currencyCode(item.deliveredCurrencyId)}
                      </span>
                    </td>
                    <td className="card-row__mono" style={{ textAlign: 'end' }}>
                      {parseFloat(item.rate).toFixed(4)}
                    </td>
                    <td className="card-row__mono" style={{ textAlign: 'end' }}>
                      {item.paymentTotal}{' '}
                      <span className="card-row__currency">
                        {currencyCode(item.paymentCurrencyId)}
                      </span>
                    </td>
                    <td className="card-row__mono" style={{ textAlign: 'end' }}>
                      {item.outstandingAmount}{' '}
                      <span className="card-row__currency">
                        {currencyCode(item.paymentCurrencyId)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge--${payStatusClass(item.paymentStatus)}`}>
                        {payStatusLabel}
                      </span>
                    </td>
                    <td>{item.reference ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(q.data?.total ?? 0) > limit ? (
        <div style={{ textAlign: 'center', marginBlockStart: 'var(--sp-4)' }}>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setLimit((l) => l + PAGE_STEP)}
            disabled={q.isFetching}
          >
            {q.isFetching ? t('common.loading') : t('contacts.profile.see_more')}
          </button>
        </div>
      ) : null}
    </>
  );
}

function payStatusClass(status: string) {
  if (status === 'PAID') return 'in';
  if (status === 'PARTIALLY_PAID') return 'warn';
  return 'danger';
}

function OverviewPanel({ contact: c }: { contact: Contact }) {
  const { t } = useTranslation();
  return (
    <div className="card-row">
      <div className="card-row__meta">
        {c.phone ? <span className="card-row__mono">{c.phone}</span> : null}
        {c.isCustomer ? <span className="badge badge--in">{t('contacts.is_customer')}</span> : null}
        {c.isSupplier ? (
          <span className="badge badge--out">{t('contacts.is_supplier')}</span>
        ) : null}
        {c.isArchived ? (
          <span className="badge badge--danger">{t('contacts.archived_badge')}</span>
        ) : null}
      </div>
      {c.notes ? <p>{c.notes}</p> : null}
      <p className="card-row__mono">
        {t('contacts.profile.created_at')}: {new Date(c.createdAt).toLocaleString()}
      </p>
    </div>
  );
}
